import _ from 'lodash'
import Vue from 'vue'
import { store } from '_/vuex/store'
import { updateValue } from '_/vuex/actions/formActions'
import { pWarn } from '_/helpers/logger'
import fieldStateHelper from '_/helpers/fieldStateHelper'
import { mapGetters } from 'vuex'
import * as fieldComponents from '_/config/fieldTypeComponents'
import * as fieldComponentsDependencies from '~/config/fieldTypeComponents'
import { isRelationFieldType } from '_/helpers/fieldHelper'

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    canCreateSearchChoice: {
      type: Boolean,
    },
    defaultValue: {
      default: false,
      type: [Boolean, String],
    },
    disabled: {
      type: Boolean,
    },
    fieldType: {
      required: true,
      type: String,
    },
    flattenToOptgroups: {
      type: Boolean,
    },
    focus: {
      default: false,
      type: Boolean,
    },
    formFieldsResetProp: {
      required: true,
      type: Object,
    },
    hidden: {
      default: false,
      type: Boolean,
    },
    id: {
      required: true,
      type: [Number, String],
    },
    inline: {
      default: false,
      type: Boolean,
    },
    isActiveEntryFilter: {
      default: null,
      type: String,
    },
    isSystem: {
      default: false,
      type: Boolean,
    },
    label: {
      default: null,
      type: String,
    },
    lazyLoading: {
      default: false,
      type: Boolean,
    },
    presentAsListed: {
      default: false,
      type: Boolean,
    },
    presentAsReference: {
      default: false,
      type: Boolean,
    },
    showLinkExistingEntries: {
      type: [Boolean],
    },
    showCreateNewEntries: {
      type: [Boolean],
    },
    multiple: {
      default: false,
      type: Boolean,
    },
    mutation: {
      required: true,
      type: String,
    },
    name: {
      required: true,
      type: String,
    },
    optionsData: {
      type: Array,
    },
    placeholder: {
      type: String,
    },
    preloadDataConfig: {
      type: Object,
    },
    preselectFirst: {
      default: false,
      type: Boolean,
    },
    relatedTableName: {
      type: [Boolean, String],
    },
    relatedTableNames: {
      type: [Array],
    },
    required: {
      type: Boolean,
    },
    supportsTranslation: {
      default: false,
      type: Boolean,
    },
    tooltip: {
      type: [Object, String],
    },
    translatable: {
      default: false,
      type: Boolean,
    },
    validationRules: {
      type: String,
    },
    value: {
      type: [
        Array,
        File,
        Number,
        Object,
        String,
        Boolean, // do not move boolean up as it introduces the 'true' bug if empty string is passed as a value
      ],
    },
    vuexModule: {
      required: true,
      type: String,
    },
  },

  /**
   * Define the components
   *
   * @type  {Object}
   */
  components: {
    ...fieldComponents,
    ...fieldComponentsDependencies,
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      ui: 'ui/ui',
    }),

    /**
     * Create the admin getter based on the vuexModule prop
     *
     * @return  {object}
     */
    admin() {
      const getterName = `${this.vuexModule}/${this.vuexModule}`

      return this.$store.getters[getterName]
    },

    /**
     * Build a list of filtered locales
     *
     * @return  {array}
     */
    filteredLocales() {
      if (_.isEmpty(this.ui.filteredLocales)) {
        return this.ui.locales
      }

      return this.ui.locales.filter(locale => {
        return _.indexOf(this.ui.filteredLocales, locale.languageCode) > -1
      })
    },

    /**
     * Gets the field type component name
     *
     * @return  {string}
     */
    getFieldTypeComponentName() {
      let data = `${this.fieldType}Field`

      if (this.presentAsListed) {
        if (data === 'RelationshipField') {
          return 'CheckboxField'
        }

        if (
          data === 'ManyToOneField' ||
          data === 'OneToManyField' ||
          data === 'OneToOneField'
        ) {
          return 'RadioField'
        }
      }

      if (this.presentAsReference) {
        return 'ReferencesField'
      }

      return data
    },

    /**
     * Gets the field disabled status
     *
     * @return  {Boolean}
     */
    getDisabledStatus() {
      return fieldStateHelper.getDisabledStatus(
        this.validationRules,
        this.disabled,
        this.admin.editedEntry
      )
    },

    /**
     * Gets the field hidden status
     *
     * @return  {Boolean}
     */
    getHiddenStatus() {
      return fieldStateHelper.getHiddenStatus(
        this.validationRules,
        this.hidden,
        this.admin.editedEntry
      )
    },

    /**
     * Builds the locales array based on translatable prop
     *
     * @return  {array}
     */
    locales() {
      if (!this.translatable) {
        return [_.find(this.ui.locales, { default: true })]
      }

      return this.ui.locales
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      computedValue: {},
      refreshFields: null,
      focusState: true,
    }
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    /**
     * Gets the value
     *
     * @param   {object}  options.locale
     * @return  {mixed}
     */
    getValue({ locale = null } = {}) {
      if (!(this.translatable || this.supportsTranslation)) {
        return this.value
      }

      return _.get(this.value, locale.languageCode, null)
    },

    /**
     * Gets the tooltip
     *
     * @param   {object}  options.locale
     * @return  {string}
     */
    getTooltip({ locale = null } = {}) {
      if (this.translatable && _.has(this.tooltip, locale.languageCode)) {
        return this.tooltip[locale.languageCode]
      }

      return this.tooltip
    },

    /**
     * Generate the field ID
     *
     * @param   {object}  options.locale
     * @return  {string}
     */
    generateId({ locale = null } = {}) {
      if (this.supportsTranslation) {
        return `${this.id}-${locale.languageCode}`
      }

      return this.id
    },

    /**
     * Determines if field should be focused
     *
     * @return  {Boolean}
     */
    isFocused(locale) {
      let focusedLocale = ''
      if (_.isEmpty(this.ui.filteredLocales)) {
        focusedLocale = this.ui.defaultLocale.languageCode
      } else {
        for (let index = 0; index < this.ui.locales.length; index++) {
          const localeItem = this.ui.locales[index]
          if (
            _.indexOf(this.ui.filteredLocales, localeItem.languageCode) > -1
          ) {
            focusedLocale = localeItem.languageCode
            break
          }
        }
      }
      return locale.languageCode === focusedLocale ? true : false
    },

    /**
     * Generates the field name
     *
     * @param   {object}  locale
     * @return  {string}
     */
    generateName(locale) {
      const name = isRelationFieldType(this.fieldType)
        ? `${this.name}-system-id`
        : this.name

      if (this.supportsTranslation) {
        return `${name}-${locale.languageCode}`
      }

      return name
    },

    /**
     * Gets the field Label
     *
     * @param   {object}  options.locale
     * @return  {string}
     */
    getFieldLabel({ locale = null } = {}) {
      if (this.translatable) {
        let fieldLabel = this.label ? this.label : this.placeholder

        fieldLabel += ' – ' + locale.name

        return fieldLabel
      }

      return this.label ? this.label : this.placeholder
    },

    /**
     * Checks if locale is filtered out
     *
     * @return  {array}
     */
    shouldShowLocale(locale) {
      if (_.isEmpty(this.ui.filteredLocales) || !this.translatable) {
        return true
      }

      if (this.checkIfFieldHasErrors(locale)) {
        return true
      }

      return _.indexOf(this.ui.filteredLocales, locale.languageCode) > -1
    },

    /**
     * Checks if field has errors
     *
     * @param   {object}  locale
     * @return  {boolean}
     */
    checkIfFieldHasErrors(locale) {
      return _.has(this.admin.error.fields, this.generateName(locale))
    },

    /**
     * Define a method that runs on change event
     *
     * @param   {integer}  options.id
     * @param   {string}  options.value
     * @return  {void}
     */
    onChange({ id, value }) {
      updateValue(
        store,
        this.mutation,
        this.id,
        this.name,
        this.prepareValue({ id, value })
      )
    },

    /**
     * Prepares the value for emmision to parent component
     *
     * @param   {integer}  options.id
     * @param   {string}  options.value
     * @return  {object}
     */
    prepareValue({ id, value }) {
      if (!(this.translatable || this.supportsTranslation)) {
        return value
      }

      if (this.admin.editorMode !== 'search') {
        const editedEntryValue = _.get(
          this.admin.editedEntry.fields,
          this.name,
          {}
        )
        if (this.admin.editorMode === 'create') {
          this.computedValue =
            this.vuexModule === 'assetsManager-main-manager'
              ? { ...this.computedValue }
              : { ...editedEntryValue }
        } else {
          this.computedValue = { ...this.computedValue, ...editedEntryValue }
        }
      }

      this.ui.locales.forEach(locale => {
        if (this.generateId({ locale }) === id) {
          this.computedValue[locale.languageCode] = value
          return this.computedValue
        }
      })

      return this.computedValue
    },

    /**
     * Determines if the field should be marked as required using a star (*)
     *
     * @return  {boolean}
     */
    shouldRenderRequiredSymbol(locale) {
      if (!this.translatable) {
        return this.required
      }

      if (locale.default || !locale.allowEmptyFields) {
        return this.required
      }
    },

    /**
     * Toggles the resetRefreshFields data property in order to force refresh of the RefreshFields component
     *
     * @return  {void}
     */
    toggleResetRefreshFields() {
      this.refreshFields = moment().valueOf()
      this.computedValue = {}
    },
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {
    this.$nextTick(() => {
      if (this.tooltip) {
        $(this.$el)
          .find('[data-toggle="tooltip"]')
          .tooltip({
            container: 'body',
          })
      }
    })
  },

  /**
   * Set the beforeDestroy hook
   *
   * @return  {void}
   */
  beforeDestroy() {
    if (this.tooltip) {
      $(this.$el)
        .find('[data-toggle="tooltip"]')
        .tooltip('destroy')
    }
  },

  /**
   * Set the watched properties
   *
   * @type  {Object}
   */
  watch: {
    /**
     * Resets the entry form
     *
     * @param   {bool}  newEntry
     * @param   {bool}  oldEntry
     * @return  {void}
     */
    formFieldsResetProp: {
      deep: true,
      handler(newEntry, oldEntry) {
        if (
          !_.isEmpty(newEntry.includeFields) &&
          _.indexOf(newEntry.includeFields, this.name) === -1
        ) {
          return
        }

        pWarn(
          'Watched property fired.',
          `FormField (${this.name})`,
          'formFieldsResetProp',
          newEntry,
          oldEntry,
          this
        )

        if (this.tooltip) {
          $(this.$el)
            .find('[data-toggle="tooltip"]')
            .tooltip({
              container: 'body',
            })
        }

        this.toggleResetRefreshFields()
      },
    },
  },
}
